import {
  type AdminProviderDetail,
  asProblem,
  CONSENT_KIND_LABELS,
  formatDate,
  formatPhone,
  HEARD_FROM_LABELS,
  LOCALE,
  PROVIDER_KIND_LABELS,
  providerDisplayName,
} from '@myhome/contracts';
import { Badge } from '@myhome/ui/components/badge';
import { Button } from '@myhome/ui/components/button';
import { ArrowLeft, Buildings } from '@myhome/ui/icons';
import { cn } from '@myhome/ui/lib/cn';
import { EmptyState } from '@myhome/ui/patterns/empty-state';
import { ListSkeleton } from '@myhome/ui/patterns/list-skeleton';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ProviderStatusBadge } from '../components/provider-status-badge';
import { adminProviderQuery } from '../lib/queries';

export const Route = createFileRoute('/_shell/providers/$providerId')({
  component: ProviderPage,
});

/**
 * One provider, as the back office sees it: the business, the listing, where
 * it works, the sign-in and the consent record. Read-only until the review
 * checklist and status changes arrive.
 */
function ProviderPage() {
  const { providerId } = Route.useParams();
  const result = useQuery(adminProviderQuery(providerId));

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/providers"
        className="inline-flex min-h-(--spacing-touch) items-center gap-1.5 self-start font-medium text-link underline-offset-4 hover:underline"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        All providers
      </Link>

      {result.isError ? (
        asProblem(result.error)?.status === 404 ? (
          <EmptyState
            icon={Buildings}
            title="No provider with that id"
            description="It may have been removed. Search the provider list instead."
          />
        ) : (
          <StatusBanner
            tone="danger"
            title="We could not load this provider"
            description="Check your connection and try again."
            action={
              <Button variant="outline" size="sm" onClick={() => result.refetch()}>
                Try again
              </Button>
            }
          />
        )
      ) : result.data === undefined ? (
        <ListSkeleton count={6} shape="row" />
      ) : (
        <ProviderDetails provider={result.data} />
      )}
    </div>
  );
}

function ProviderDetails({ provider }: { provider: AdminProviderDetail }) {
  return (
    <>
      <Heading provider={provider} />
      <Banners provider={provider} />
      <div className="grid gap-4 lg:grid-cols-2">
        <BusinessCard provider={provider} />
        <ContactCard provider={provider} />
        <CoverageCard provider={provider} />
        <ReviewCard provider={provider} />
      </div>
      <ListingSection provider={provider} />
      <ConsentsSection provider={provider} />
    </>
  );
}

/** Date and time, for events. */
const DATE_TIME = { year: 'numeric', hour: '2-digit', minute: '2-digit' } as const;

function Heading({ provider }: { provider: AdminProviderDetail }) {
  return (
    <header className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-headline font-semibold text-navy-900">
          {providerDisplayName(provider)}
        </h1>
        <ProviderStatusBadge status={provider.status} />
      </div>
      <p className="text-neutral-600">
        {PROVIDER_KIND_LABELS[provider.kind]} · signed up{' '}
        {formatDate(new Date(provider.createdAt), { year: 'numeric' })} ·{' '}
        {provider.lastLoginAt === null
          ? 'never signed in'
          : `last signed in ${formatDate(new Date(provider.lastLoginAt), DATE_TIME)}`}
      </p>
    </header>
  );
}

function Banners({ provider }: { provider: AdminProviderDetail }) {
  return (
    <>
      {provider.accountStatus === 'ACTIVE' ? null : (
        <StatusBanner tone="danger" title="This provider's sign-in is blocked" />
      )}
      {provider.status === 'REJECTED' && provider.rejectionReason !== null ? (
        <StatusBanner
          tone="danger"
          title="Not approved"
          description={`Reason given: ${provider.rejectionReason}`}
        />
      ) : null}
    </>
  );
}

function BusinessCard({ provider }: { provider: AdminProviderDetail }) {
  return (
    <Card title="Business">
      <Row label="Registered name">{provider.legalName}</Row>
      <Row label="Trading name">{orNone(provider.tradingName)}</Row>
      <Row label="CIPC registration">
        <span className="tabular-nums">{orNone(provider.registrationNo)}</span>
      </Row>
      <Row label="VAT number">
        <span className="tabular-nums">{orNone(provider.vatNo)}</span>
      </Row>
      <Row label="Trading since">
        {provider.activeSince === null
          ? orNone(null)
          : formatDate(new Date(`${provider.activeSince}T00:00:00Z`), { year: 'numeric' })}
      </Row>
      <Row label="Based in">
        {provider.basedInArea === null
          ? orNone(null)
          : `${provider.basedInArea.name}, ${provider.basedInArea.city}`}
      </Row>
    </Card>
  );
}

function ContactCard({ provider }: { provider: AdminProviderDetail }) {
  return (
    <Card title="Contact">
      <Row label="Contact person">
        {provider.contactFirstName} {provider.contactLastName}
      </Row>
      <Row label="Phone members call">
        <a href={`tel:${provider.phone}`} className="tabular-nums text-link underline underline-offset-4">
          {formatPhone(provider.phone)}
        </a>
      </Row>
      <Row label="Email for quotes">
        <span className="break-all">{provider.email}</span>
      </Row>
      <Row label="Sign-in email">
        <span className="break-all">{provider.accountEmail}</span>
        <Badge variant={provider.emailVerified ? 'success' : 'warning'} className="mt-1 self-start">
          {provider.emailVerified ? 'Verified' : 'Not verified'}
        </Badge>
      </Row>
      <Row label="Heard about us from">{HEARD_FROM_LABELS[provider.heardFrom]}</Row>
    </Card>
  );
}

function CoverageCard({ provider }: { provider: AdminProviderDetail }) {
  return (
    <Card title="Where they work">
      <Row label="Categories">
        {provider.categories.length === 0
          ? orNone(null)
          : provider.categories.map((category) => category.name).join(', ')}
      </Row>
      <Row label={`Service areas (${provider.serviceAreas.length})`}>
        {provider.serviceAreas.length === 0
          ? orNone(null)
          : provider.serviceAreas.map((area) => area.name).join(', ')}
      </Row>
    </Card>
  );
}

function ReviewCard({ provider }: { provider: AdminProviderDetail }) {
  return (
    <Card title="Review">
      <Row label="Submitted for review">
        {provider.submittedAt === null
          ? orNone(null, 'Not yet')
          : formatDate(new Date(provider.submittedAt), DATE_TIME)}
      </Row>
      <Row label="Last review">
        {provider.reviewedAt === null
          ? orNone(null, 'Not reviewed yet')
          : `${formatDate(new Date(provider.reviewedAt), DATE_TIME)} by ${provider.reviewerName ?? 'a removed admin'}`}
      </Row>
      <Row label="Internal notes">{orNone(provider.reviewNotes)}</Row>
      <Row label="MyHome+ liaison">{orNone(provider.liaisonAdminName, 'Not assigned')}</Row>
      <Row label="Google listing">{orNone(provider.googlePlaceId, 'Not matched')}</Row>
    </Card>
  );
}

function ListingSection({ provider }: { provider: AdminProviderDetail }) {
  return (
    <section aria-labelledby="provider-listing" className="flex flex-col gap-2">
      <h2 id="provider-listing" className="font-heading text-title font-semibold text-navy-900">
        Listing
      </h2>
      <dl className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
        <Row label="Overview">
          {provider.overview === null ? (
            orNone(null)
          ) : (
            <span className="whitespace-pre-line font-normal">{provider.overview}</span>
          )}
        </Row>
        <Row label="Services">
          {provider.services.length === 0 ? orNone(null) : provider.services.join(', ')}
        </Row>
        <Row label="Payment methods">
          {provider.paymentMethods.length === 0 ? orNone(null) : provider.paymentMethods.join(', ')}
        </Row>
        <Row label="Member discount">
          {provider.discountPercent === null && provider.discountText === null
            ? orNone(null)
            : [
                provider.discountPercent === null ? null : `${provider.discountPercent}%`,
                provider.discountText,
              ]
                .filter((part) => part !== null)
                .join(' · ')}
        </Row>
        <Row label="Call-out fee">
          {provider.callOutFee === null ? orNone(null, 'Not answered') : provider.callOutFee ? 'Yes' : 'No'}
        </Row>
        <Row label="Usual response time">
          {provider.responseTimeMinutes === null
            ? orNone(null)
            : `${provider.responseTimeMinutes.toLocaleString(LOCALE)} minutes`}
        </Row>
        <Row label="Emergency call-outs">{provider.emergencyAvailable ? 'Yes' : 'No'}</Row>
        {provider.kind === 'ATTORNEY' ? (
          <Row label="Hourly rate">
            {provider.hourlyRateCents === null ? (
              orNone(null)
            ) : (
              <span className="tabular-nums">{formatRands(provider.hourlyRateCents)}</span>
            )}
          </Row>
        ) : null}
      </dl>
    </section>
  );
}

function ConsentsSection({ provider }: { provider: AdminProviderDetail }) {
  return (
    <section aria-labelledby="provider-consents" className="flex flex-col gap-2">
      <div>
        <h2 id="provider-consents" className="font-heading text-title font-semibold text-navy-900">
          Consent record
        </h2>
        <p className="text-neutral-600">Every choice the provider has made, newest first.</p>
      </div>
      <div className="overflow-x-auto rounded-(--radius-card) border border-border bg-surface">
        <table className="w-full border-collapse text-left">
          <thead className="border-b border-border bg-navy-50">
            <tr>
              {['Consent', 'Choice', 'Version', 'When'].map((heading) => (
                <th
                  key={heading}
                  scope="col"
                  className="px-3 py-2 text-eyebrow font-semibold text-neutral-700 uppercase tracking-[0.04em]"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {provider.consents.map((consent) => (
              <tr key={`${consent.kind}-${consent.at}`} className="border-t border-border first:border-t-0">
                <td className="px-3 py-2">{CONSENT_KIND_LABELS[consent.kind]}</td>
                <td className="px-3 py-2">
                  {consent.granted ? (
                    <Badge variant="success">{consent.kind === 'MARKETING' ? 'Opted in' : 'Accepted'}</Badge>
                  ) : (
                    <Badge variant="neutral">{consent.kind === 'MARKETING' ? 'Opted out' : 'Declined'}</Badge>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums">{consent.version}</td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                  {formatDate(new Date(consent.at), DATE_TIME)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** A missing value, said in words rather than left blank. */
function orNone(value: string | null, empty = 'Not given') {
  return value ?? <span className="font-normal text-neutral-600">{empty}</span>;
}

/** R1 250 (DESIGN.md "Type"). */
function formatRands(cents: number): string {
  return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'ZAR' })
    .format(cents / 100)
    .replace(/^R\s/, 'R');
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  const id = `provider-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h2 id={id} className="font-heading text-title font-semibold text-navy-900">
        {title}
      </h2>
      <dl className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">{children}</dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-0.5 border-t border-border px-4 py-2.5 first:border-t-0')}>
      <dt className="text-eyebrow text-neutral-600">{label}</dt>
      <dd className="flex flex-col font-medium text-navy-900">{children}</dd>
    </div>
  );
}
